<template>
  <div class="etiquetas-page">
    <!-- Cabecera -->
    <div class="etq-header mb-2">
      <div class="d-flex align-items-center">
        <div class="etq-header__icon">
          <i class="fa-solid fa-tags"></i>
        </div>
        <div>
          <h2 class="mb-0 font-weight-bolder">Etiquetas</h2>
          <small class="text-muted">SKUs de Shopify · impresión ZPL vía PrintNode</small>
        </div>
      </div>
      <div class="etq-header__stats">
        <div class="etq-stat">
          <span class="etq-stat__num">{{ items.length }}</span>
          <span class="etq-stat__label">SKUs</span>
        </div>
        <div class="etq-stat">
          <span class="etq-stat__num text-primary">{{ totalEtiquetas }}</span>
          <span class="etq-stat__label">etiquetas</span>
        </div>
      </div>
    </div>

    <b-row>
      <!-- ===================== Columna izquierda ===================== -->
      <b-col lg="8">
        <!-- Paso 1: Añadir SKUs -->
        <b-card no-body class="mb-2">
          <div class="etq-card-head">
            <div class="d-flex align-items-center">
              <span class="step-num">1</span>
              <span class="font-weight-bold">Añadir SKUs</span>
            </div>
            <b-button
              v-if="items.length"
              size="sm"
              variant="flat-danger"
              class="btn-icon-text"
              @click="items = []"
            >
              <i class="fa-solid fa-trash-can"></i> Vaciar
            </b-button>
          </div>

          <b-card-body class="pt-1">
            <b-input-group class="etq-add-group mb-75">
              <b-input-group-prepend is-text>
                <i class="fa-solid fa-barcode text-muted"></i>
              </b-input-group-prepend>
              <b-form-input
                v-model="nuevoSku"
                placeholder="Escribe un SKU y pulsa Enter"
                @keyup.enter="agregarSku"
              />
              <b-input-group-append>
                <b-button variant="primary" @click="agregarSku">
                  <i class="fa-solid fa-plus mr-25"></i> Añadir
                </b-button>
              </b-input-group-append>
            </b-input-group>

            <b-button
              size="sm"
              variant="flat-secondary"
              class="px-50"
              @click="mostrarBulk = !mostrarBulk"
            >
              <i class="fa-solid" :class="mostrarBulk ? 'fa-chevron-down' : 'fa-list'"></i>
              Pegar varios SKUs
            </b-button>

            <div v-show="mostrarBulk" class="mt-1">
              <b-form-textarea
                v-model="bulkSkus"
                placeholder="Uno por línea o separados por comas"
                rows="3"
                class="mb-75"
              />
              <b-button size="sm" variant="outline-primary" @click="agregarBulk">
                <i class="fa-solid fa-plus mr-25"></i> Añadir lista
              </b-button>
            </div>

            <b-button
              size="sm"
              variant="flat-secondary"
              class="px-50 ml-50"
              @click="mostrarManual = !mostrarManual"
            >
              <i class="fa-solid" :class="mostrarManual ? 'fa-chevron-down' : 'fa-pen'"></i>
              Etiqueta manual
            </b-button>

            <div v-show="mostrarManual" class="mt-1">
              <b-row>
                <b-col cols="12" md="7">
                  <b-form-group label="Título" label-class="small font-weight-bold" class="mb-75">
                    <b-form-input v-model="manualTitle" placeholder="Nombre del producto" @keyup.enter="agregarManual" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="5">
                  <b-form-group label="Precio (GBP)" label-class="small font-weight-bold" class="mb-75">
                    <b-input-group prepend="£">
                      <b-form-input v-model="manualPrice" type="number" step="0.01" min="0" placeholder="0.00" @keyup.enter="agregarManual" />
                    </b-input-group>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-button size="sm" variant="outline-primary" @click="agregarManual">
                <i class="fa-solid fa-plus mr-25"></i> Añadir manual
              </b-button>
              <small class="text-muted d-block mt-50">El resto (EUR, USD y WSP) se calcula automáticamente.</small>
            </div>

            <b-button
              size="sm"
              variant="flat-secondary"
              class="px-50 ml-50"
              @click="abrirPedidos"
            >
              <i class="fa-solid fa-cart-shopping"></i>
              Cargar de pedido
            </b-button>
          </b-card-body>

          <!-- Cola de SKUs -->
          <div v-if="items.length === 0" class="etq-empty">
            <i class="fa-solid fa-tags"></i>
            <p class="mb-0 font-weight-bold">No hay SKUs en la cola</p>
            <small class="text-muted">Añade un SKU arriba para empezar</small>
          </div>

          <b-table-lite
            v-else
            :items="items"
            :fields="columnas"
            class="etq-table mb-0"
            responsive
          >
            <template #cell(sku)="data">
              <div class="d-flex align-items-center">
                <b-badge v-if="data.item.manual" variant="light-info">
                  <i class="fa-solid fa-pen mr-25"></i> manual
                </b-badge>
                <code v-else class="etq-sku" :class="{ 'text-danger': data.item.noEncontrado }">{{ data.item.sku }}</code>
                <b-badge
                  v-if="data.item.pedido"
                  variant="light-primary"
                  class="ml-50"
                  v-b-tooltip.hover
                  title="Se imprime en la etiqueta"
                >
                  #{{ data.item.pedido }}
                </b-badge>
                <b-badge
                  v-if="data.item.noEncontrado"
                  variant="light-danger"
                  class="ml-50"
                  v-b-tooltip.hover
                  title="No encontrado en Shopify"
                >
                  <i class="fa-solid fa-triangle-exclamation"></i>
                </b-badge>
              </div>
            </template>

            <template #cell(title)="data">
              <span v-if="data.item.title">{{ data.item.title }}</span>
              <span v-else-if="data.item.noEncontrado" class="text-muted">—</span>
              <b-spinner v-else small variant="secondary" />
            </template>

            <template #cell(price)="data">
              <b-input-group size="sm" prepend="£" class="etq-price-input">
                <b-form-input
                  v-model="data.item.price"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="—"
                  @change="cargarPreviews()"
                />
              </b-input-group>
            </template>

            <template #cell(ean)="data">
              <code v-if="data.item.ean" class="text-muted">{{ data.item.ean }}</code>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(copies)="data">
              <b-form-spinbutton
                v-model="data.item.copies"
                min="1"
                max="999"
                size="sm"
                inline
                class="etq-spin"
              />
            </template>

            <template #cell(acciones)="data">
              <b-button
                variant="flat-danger"
                size="sm"
                class="btn-icon"
                @click="quitarSku(data.index)"
              >
                <i class="fa-solid fa-trash"></i>
              </b-button>
            </template>
          </b-table-lite>
        </b-card>

        <!-- Paso 2: Diseño -->
        <b-card no-body>
          <div class="etq-card-head">
            <div class="d-flex align-items-center">
              <span class="step-num">2</span>
              <span class="font-weight-bold">Elegir diseño</span>
            </div>
            <b-button
              size="sm"
              variant="flat-secondary"
              :disabled="cargandoPreview"
              @click="cargarPreviews"
            >
              <b-spinner v-if="cargandoPreview" small />
              <i v-else class="fa-solid fa-rotate"></i>
              Actualizar
            </b-button>
          </div>

          <b-card-body>
            <b-row>
              <b-col
                v-for="d in designs"
                :key="d.id"
                cols="6"
                class="mb-1"
              >
                <div
                  class="design-card"
                  :class="{ selected: design === d.id }"
                  @click="design = d.id"
                >
                  <div class="design-check" v-if="design === d.id">
                    <i class="fa-solid fa-check"></i>
                  </div>
                  <div class="design-preview">
                    <img v-if="previews[d.id]" :src="previews[d.id]" />
                    <b-spinner v-else-if="cargandoPreview" small variant="secondary" />
                    <i v-else class="fa-solid fa-image text-muted"></i>
                  </div>
                  <div class="design-title">{{ d.label }}</div>
                </div>
              </b-col>
            </b-row>

            <div v-if="design === 'rrp'" class="d-flex align-items-center mt-50">
              <label class="small font-weight-bold mb-0 mr-75">Moneda del RRP</label>
              <b-form-select
                v-model="rrpMoneda"
                :options="[
                  { value: 'GBP', text: '£ GBP' },
                  { value: 'EUR', text: '€ EUR' },
                  { value: 'USD', text: '$ USD' },
                ]"
                size="sm"
                style="max-width: 140px"
                @change="cargarPreviews"
              />
            </div>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- ===================== Columna derecha ===================== -->
      <b-col lg="4">
        <div class="etq-sticky">
          <!-- Vista previa grande del diseño seleccionado -->
          <b-card no-body class="mb-2 etq-preview-card">
            <b-card-body class="text-center">
              <div class="d-flex justify-content-between align-items-center mb-75">
                <small class="text-muted font-weight-bold text-uppercase">Vista previa</small>
                <b-badge variant="light-primary">{{ nombreDiseno }}</b-badge>
              </div>
              <div class="etq-big-preview">
                <img v-if="previews[design]" :src="previews[design]" />
                <b-spinner v-else-if="cargandoPreview" variant="primary" />
                <span v-else class="text-muted small">Sin vista previa</span>
              </div>
              <small class="text-muted d-block mt-75">
                Datos de: <strong>{{ muestra.sku || muestra.name }}</strong>
              </small>
            </b-card-body>
          </b-card>

          <!-- Impresora -->
          <b-card no-body class="mb-2">
            <b-card-body>
              <label class="font-weight-bold d-flex align-items-center mb-75">
                <i class="fa-solid fa-print mr-50 text-primary"></i> Impresora
              </label>
              <b-form-select
                v-model="printerId"
                :options="printerOptions"
                @change="guardarImpresora"
              >
                <template #first>
                  <b-form-select-option :value="null" disabled>
                    -- Selecciona impresora --
                  </b-form-select-option>
                </template>
              </b-form-select>
              <div class="d-flex justify-content-between align-items-center mt-75">
                <small v-if="printerId" class="text-success font-weight-bold">
                  <i class="fa-solid fa-circle-check"></i> Guardada
                </small>
                <small v-else class="text-muted">Sin guardar</small>
                <b-button size="sm" variant="flat-secondary" class="px-50" @click="cargarImpresoras">
                  <i class="fa-solid fa-rotate"></i> Recargar
                </b-button>
              </div>
            </b-card-body>
          </b-card>

          <!-- Ajustes -->
          <b-card no-body class="mb-2">
            <div class="etq-card-head">
              <span class="font-weight-bold">
                <i class="fa-solid fa-sliders mr-50 text-primary"></i> Ajustes
              </span>
            </div>
            <b-card-body class="pt-1">
              <b-row>
                <b-col cols="6">
                  <b-form-group label="Tasa GBP→EUR" label-class="small">
                    <b-form-input v-model="rateEur" type="number" step="0.01" size="sm" @change="cargarPreviews" />
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group label="Tasa GBP→USD" label-class="small">
                    <b-form-input v-model="rateUsd" type="number" step="0.01" size="sm" @change="cargarPreviews" />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row class="align-items-center">
                <b-col cols="6">
                  <b-form-group label="Darkness (0-30)" label-class="small" class="mb-0">
                    <b-form-input v-model="darkness" type="number" min="0" max="30" size="sm" />
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-checkbox v-model="withLogo" switch class="mt-1" @change="cargarPreviews">
                    {{ withLogo ? 'Con logo' : 'Sin logo' }}
                  </b-form-checkbox>
                </b-col>
              </b-row>
              <div class="etq-note mt-1">
                <i class="fa-solid fa-circle-info mr-25"></i>
                WSP = 40% del RRP (60% dto.) · importes redondeados sin decimales
              </div>
            </b-card-body>
          </b-card>

          <!-- Imprimir -->
          <b-button
            block
            size="lg"
            variant="primary"
            class="etq-print-btn"
            :disabled="imprimiendo || items.length === 0 || !printerId"
            @click="imprimir"
          >
            <b-spinner v-if="imprimiendo" small class="mr-50" />
            <i v-else class="fa-solid fa-print mr-50"></i>
            Imprimir {{ totalEtiquetas }} {{ totalEtiquetas === 1 ? 'etiqueta' : 'etiquetas' }}
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Modal: cargar SKUs desde un pedido del escáner -->
    <b-modal
      v-model="modalPedidos"
      title="Cargar SKUs de un pedido"
      hide-footer
      size="lg"
    >
      <b-input-group class="mb-1">
        <b-form-input
          v-model="busquedaPedido"
          placeholder="Buscar por cliente o email"
          @keyup.enter="cargarPedidos"
        />
        <b-input-group-append>
          <b-button variant="outline-primary" @click="cargarPedidos">
            <i class="fa-solid fa-magnifying-glass"></i>
          </b-button>
        </b-input-group-append>
      </b-input-group>

      <div v-if="cargandoPedidos" class="text-center py-2">
        <b-spinner variant="primary" />
      </div>
      <div v-else-if="pedidos.length === 0" class="text-center text-muted py-2">
        No hay pedidos
      </div>
      <b-list-group v-else class="etq-pedidos-list">
        <b-list-group-item
          v-for="p in pedidos"
          :key="p.id"
          button
          @click="cargarPedido(p)"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="font-weight-bold">
                {{ (p.cliente && (p.cliente.empresa || ((p.cliente.nombre || '') + ' ' + (p.cliente.apellidos || '')).trim())) || 'Sin cliente' }}
              </span>
              <small class="text-muted d-block">
                #{{ p.id }} · {{ p.createdAt }}
              </small>
            </div>
            <div class="text-right">
              <b-badge variant="light-primary">
                {{ (p.items && p.items.length) || 0 }} SKUs
              </b-badge>
              <small class="text-muted d-block mt-25">
                {{ p.simbolo }}{{ p.total }}
              </small>
            </div>
          </div>
        </b-list-group-item>
      </b-list-group>
    </b-modal>
  </div>
</template>

<script>
import axios from '@axios'

export default {
  data() {
    return {
      nuevoSku: '',
      bulkSkus: '',
      mostrarBulk: false,
      mostrarManual: false,
      manualTitle: '',
      manualPrice: '',
      items: [], // [{ sku, copies, title, price, ean, noEncontrado, manual }]
      printers: [],
      printerId: null,
      designs: [],
      design: 'mayorista',
      previews: {}, // { designId: dataUrl }
      rateEur: '1.17',
      rateUsd: '1.35',
      rrpMoneda: 'GBP',
      darkness: 10,
      withLogo: true,
      imprimiendo: false,
      cargandoPreview: false,
      modalPedidos: false,
      pedidos: [],
      busquedaPedido: '',
      cargandoPedidos: false,
      columnas: [
        { key: 'sku', label: 'SKU' },
        { key: 'title', label: 'Título' },
        { key: 'price', label: 'Precio', thStyle: { width: '130px' } },
        { key: 'ean', label: 'EAN', thStyle: { width: '130px' } },
        { key: 'copies', label: 'Cant.', thStyle: { width: '120px' } },
        { key: 'acciones', label: '', thStyle: { width: '50px' } },
      ],
    }
  },
  computed: {
    totalEtiquetas() {
      return this.items.reduce((acc, it) => acc + (Number(it.copies) || 1), 0)
    },
    printerOptions() {
      return this.printers.map(p => ({
        value: p.id,
        text: `${p.name}${p.computer ? ' (' + p.computer + ')' : ''}${p.state ? ' · ' + p.state : ''}`,
      }))
    },
    nombreDiseno() {
      const d = this.designs.find(x => x.id === this.design)
      return d ? d.label : this.design
    },
    // Datos de muestra para la preview (primer SKU, o ejemplo si no hay)
    muestra() {
      const it = this.items[0]
      if (it) return { name: it.title, sku: it.sku, ean: it.ean, price: it.price, pedido: it.pedido || '' }
      return { name: 'Leather Bag', sku: 'EJEMPLO-SKU', ean: '5060985229860', price: '999' }
    },
  },
  created() {
    axios.post('/uso', { seccion: 'etiquetas' }).catch(() => {})
    this.cargarImpresoras()
    this.cargarDesigns()
  },
  methods: {
    notificar(titulo, variant = 'success') {
      this.$bvToast.toast(titulo, {
        title: variant === 'danger' ? 'Error' : 'Etiquetas',
        variant,
        solidColor: true,
      })
    },
    agregarManual() {
      const title = (this.manualTitle || '').trim()
      const price = (this.manualPrice == null ? '' : String(this.manualPrice)).trim()
      if (!title || !price) {
        this.notificar('Indica título y precio en GBP', 'danger')
        return
      }
      this.items.push({
        sku: '', title, price, ean: '', copies: 1, manual: true, noEncontrado: false,
      })
      this.manualTitle = ''
      this.manualPrice = ''
      this.cargarPreviews()
    },
    agregarSku() {
      const sku = (this.nuevoSku || '').trim()
      if (!sku) return
      this.agregarSkus([sku])
      this.nuevoSku = ''
    },
    agregarBulk() {
      const trozos = (this.bulkSkus || '')
        .split(/[\n,;]+/)
        .map(s => s.trim())
        .filter(Boolean)
      this.agregarSkus(trozos)
      this.bulkSkus = ''
    },
    // Quita caracteres invisibles (espacio no separable, zero-width, BOM) que
    // se cuelan al pegar listas y rompen la coincidencia del SKU en Shopify.
    limpiarSku(s) {
      return String(s == null ? '' : s)
        .replace(/[\u00A0\u200B\u200C\u200D\uFEFF]/g, '')
        .trim()
    },
    agregarSkus(skus) {
      const nuevos = []
      skus.forEach(raw => {
        const sku = this.limpiarSku(raw)
        if (!sku) return
        const existente = this.items.find(it => it.sku.toLowerCase() === sku.toLowerCase())
        if (existente) {
          existente.copies = (Number(existente.copies) || 1) + 1
        } else {
          const item = { sku, copies: 1, title: '', price: '', ean: '', noEncontrado: false }
          this.items.push(item)
          nuevos.push(item)
        }
      })
      if (nuevos.length > 0) this.consultarShopify(nuevos)
    },
    consultarShopify(itemsNuevos) {
      const skus = itemsNuevos.map(it => it.sku).join(',')
      axios
        .get('/etiquetas/lookup', { params: { sku: skus } })
        .then(({ data }) => {
          (data || []).forEach(d => {
            const item = this.items.find(it => it.sku.toLowerCase() === (d.sku || '').toLowerCase())
            if (!item) return
            item.title = d.title || ''
            item.price = d.price || ''
            item.ean = d.ean || ''
            item.noEncontrado = !!d.noEncontrado
          })
          // refrescar la preview con datos reales del primer SKU
          this.cargarPreviews()
        })
        .catch(() => {
          this.notificar('No se pudieron consultar los datos en Shopify', 'danger')
        })
    },
    cargarDesigns() {
      axios
        .get('/etiquetas/designs')
        .then(({ data }) => {
          this.designs = data || []
          this.cargarPreviews()
        })
        .catch(() => {})
    },
    cargarImpresoras() {
      // Primero la impresora guardada en BD, luego la lista de PrintNode
      axios
        .get('/etiquetas/config')
        .then(({ data }) => {
          if (data && data.printerId) this.printerId = data.printerId
        })
        .catch(() => {})
        .finally(() => {
          axios
            .get('/etiquetas/printers')
            .then(({ data }) => {
              this.printers = data || []
              const existeGuardada = this.printerId && this.printers.some(p => p.id === this.printerId)
              const porDefecto = this.printers.find(p => p.default)
              if (!existeGuardada) {
                this.printerId = porDefecto ? porDefecto.id : (this.printers[0] && this.printers[0].id) || null
              }
            })
            .catch(e => {
              const msg = (e.response && e.response.data && e.response.data.msg) || 'No se pudieron cargar las impresoras'
              this.notificar(msg, 'danger')
            })
        })
    },
    guardarImpresora() {
      axios
        .put('/etiquetas/config', { printerId: this.printerId })
        .then(() => {
          this.notificar('Impresora guardada')
        })
        .catch(() => {
          this.notificar('No se pudo guardar la impresora', 'danger')
        })
    },
    // Genera la preview de todos los diseños con los datos de muestra
    cargarPreviews() {
      if (this.designs.length === 0) return
      this.cargandoPreview = true
      const base = {
        ...this.muestra,
        rateEur: this.rateEur,
        rateUsd: this.rateUsd,
        rrpMoneda: this.rrpMoneda,
        withLogo: this.withLogo,
        darkness: this.darkness,
      }
      // Secuencial con pausa para no saturar Labelary (rate limit)
      const ids = this.designs.map(d => d.id)
      const siguiente = (i) => {
        if (i >= ids.length) { this.cargandoPreview = false; return }
        axios
          .post('/etiquetas/preview', { ...base, design: ids[i] })
          .then(({ data }) => {
            if (data && data.image) this.$set(this.previews, ids[i], data.image)
          })
          .catch(() => {})
          .finally(() => setTimeout(() => siguiente(i + 1), 300))
      }
      siguiente(0)
    },
    quitarSku(index) {
      this.items.splice(index, 1)
    },
    abrirPedidos() {
      this.modalPedidos = true
      if (this.pedidos.length === 0) this.cargarPedidos()
    },
    cargarPedidos() {
      this.cargandoPedidos = true
      axios
        .get('/escaner/pedidos', { params: { q: this.busquedaPedido || undefined, limit: 50 } })
        .then(({ data }) => {
          this.pedidos = data || []
        })
        .catch(() => {
          this.notificar('No se pudieron cargar los pedidos', 'danger')
        })
        .finally(() => {
          this.cargandoPedidos = false
        })
    },
    // Vuelca los items del pedido a la cola: copies = qty del pedido
    cargarPedido(pedido) {
      const items = Array.isArray(pedido.items) ? pedido.items : []
      const nuevos = []
      let cargados = 0
      items.forEach(it => {
        const sku = this.limpiarSku(it.sku)
        if (!sku) return
        cargados += 1
        const qty = Math.max(1, parseInt(it.qty, 10) || 1)
        const existente = this.items.find(x => x.sku.toLowerCase() === sku.toLowerCase())
        if (existente) {
          existente.copies = (Number(existente.copies) || 1) + qty
          this.$set(existente, 'pedido', pedido.id)
        } else {
          const item = { sku, copies: qty, title: '', price: '', ean: '', noEncontrado: false, pedido: pedido.id }
          this.items.push(item)
          nuevos.push(item)
        }
      })
      this.modalPedidos = false
      if (cargados === 0) {
        this.notificar('El pedido no tiene SKUs', 'danger')
        return
      }
      if (nuevos.length > 0) this.consultarShopify(nuevos)
      this.notificar(`Cargados ${cargados} SKUs del pedido #${pedido.id}`)
    },
    imprimir() {
      if (this.items.length === 0 || !this.printerId) return
      this.imprimiendo = true
      axios
        .post('/etiquetas/print', {
          items: this.items,
          printerId: this.printerId,
          design: this.design,
          rateEur: this.rateEur,
          rateUsd: this.rateUsd,
          rrpMoneda: this.rrpMoneda,
          darkness: this.darkness,
          withLogo: this.withLogo,
        })
        .then(({ data }) => {
          let msg = `Enviadas ${this.totalEtiquetas} etiquetas a imprimir`
          if (data && data.noEncontrados && data.noEncontrados.length) {
            msg += ` (sin datos Shopify: ${data.noEncontrados.join(', ')})`
          }
          this.notificar(msg)
        })
        .catch(e => {
          const msg = (e.response && e.response.data && e.response.data.msg) || 'Error al imprimir'
          this.notificar(msg, 'danger')
        })
        .finally(() => {
          this.imprimiendo = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
$accent: #7367f0;

.etiquetas-page {
  padding-bottom: 2rem;
}

.etq-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  &__icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: linear-gradient(135deg, #7367f0, #9e95f5);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-right: 1rem;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.35);
  }

  &__stats {
    display: flex;
    gap: 1.5rem;
  }
}

.etq-stat {
  text-align: center;
  line-height: 1.1;

  &__num {
    display: block;
    font-size: 1.6rem;
    font-weight: 700;
  }
  &__label {
    font-size: 0.75rem;
    color: #b9b9c3;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
}

.etq-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #ebe9f1;
}

.step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(115, 103, 240, 0.12);
  color: $accent;
  font-size: 0.8rem;
  font-weight: 700;
  margin-right: 0.6rem;
}

.etq-add-group {
  ::v-deep .input-group-text {
    background: #fff;
  }
}

.etq-empty {
  text-align: center;
  padding: 2.5rem 1rem 3rem;
  color: #b9b9c3;

  i {
    font-size: 2.5rem;
    opacity: 0.4;
    margin-bottom: 0.75rem;
    display: block;
  }
}

.etq-table {
  ::v-deep td {
    vertical-align: middle;
    padding-top: 0.6rem;
    padding-bottom: 0.6rem;
  }
  ::v-deep thead th {
    font-size: 0.72rem;
    letter-spacing: 0.05em;
  }
}

.etq-sku {
  font-size: 0.82rem;
  font-weight: 600;
  background: transparent;
  padding: 0;
}

.etq-price-input {
  width: 110px;
}
.etq-price-input input::-webkit-outer-spin-button,
.etq-price-input input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}

.etq-spin {
  ::v-deep .b-form-spinbutton {
    max-width: 110px;
  }
}

/* --- Tarjetas de diseño --- */
.design-card {
  position: relative;
  border: 2px solid #ebe9f1;
  border-radius: 10px;
  padding: 0.6rem;
  cursor: pointer;
  transition: all 0.18s ease;
  background: #fff;

  &:hover {
    border-color: #c8c2f7;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  }

  &.selected {
    border-color: $accent;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.18);
  }
}

.design-check {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: $accent;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  z-index: 2;
  box-shadow: 0 2px 6px rgba(115, 103, 240, 0.5);
}

.design-preview {
  background: #f8f8fc;
  border-radius: 6px;
  min-height: 86px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;

  img {
    max-width: 100%;
    max-height: 90px;
    image-rendering: pixelated;
  }
}

.design-title {
  font-size: 0.78rem;
  font-weight: 600;
  text-align: center;
  margin-top: 0.5rem;
}

.etq-pedidos-list {
  max-height: 55vh;
  overflow-y: auto;
}

/* --- Panel derecho --- */
.etq-sticky {
  position: sticky;
  top: 100px;
}

.etq-preview-card {
  border: 1px solid #ebe9f1;
}

.etq-big-preview {
  background: #fff;
  border: 1px dashed #d8d6e0;
  border-radius: 8px;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;

  img {
    max-width: 100%;
    image-rendering: pixelated;
  }
}

.etq-note {
  font-size: 0.72rem;
  color: #6e6b7b;
  background: rgba(115, 103, 240, 0.08);
  border-radius: 6px;
  padding: 0.5rem 0.7rem;
  line-height: 1.4;
}

.etq-print-btn {
  font-weight: 600;
  box-shadow: 0 6px 16px rgba(115, 103, 240, 0.4);
}

@media (max-width: 991px) {
  .etq-sticky {
    position: static;
  }
}
</style>
